import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { locationAPI, listingAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import PropertyCard from '../components/PropertyCard';
import LocationCard from '../components/LocationCard';
import { getLocationVisuals, FALLBACK_VISUALS } from '../lib/locationVisuals';
import { getLocationCopy } from '../lib/locationDisplay';
import AreaIntelligenceModule from '../components/areaIntelligence/AreaIntelligenceModule';
import { Button } from '../components/ui/button';
import {
  MapPin,
  Building2,
  ArrowLeft,
  Loader2,
  ExternalLink,
  Info,
  CheckCircle2,
} from 'lucide-react';

export default function LocationsPage() {
  const { locationId } = useParams();
  const { t, locale } = useLanguage();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationListings, setLocationListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [heroImgSrc, setHeroImgSrc] = useState(null);

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    if (locationId) {
      loadLocationDetails(locationId);
    } else {
      setSelectedLocation(null);
    }
  }, [locationId]);

  // Reset hero image when location or locale changes
  useEffect(() => {
    if (selectedLocation) {
      const visuals = getLocationVisuals(selectedLocation.name, locale);
      setHeroImgSrc(visuals.heroImage);
    }
  }, [selectedLocation, locale]);

  const loadLocations = async () => {
    try {
      const response = await locationAPI.getAll({ active_only: true });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to load locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLocationDetails = async (id) => {
    setListingsLoading(true);
    try {
      const [locationRes, listingsRes] = await Promise.all([
        locationAPI.getById(id),
        listingAPI.getAll({ location_id: id }),
      ]);
      setSelectedLocation(locationRes.data);
      setLocationListings(listingsRes.data);
    } catch (error) {
      console.error('Failed to load location details:', error);
    } finally {
      setListingsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  // ── Location Detail View ──────────────────────────────────────────────────
  if (locationId && selectedLocation) {
    const visuals = getLocationVisuals(selectedLocation.name, locale);
    const locationCopy = getLocationCopy(selectedLocation, locale);
    const fallbackHero = visuals.fallback || FALLBACK_VISUALS.heroImage;

    return (
      <div className="min-h-screen bg-slate-100 pt-20" data-testid="location-detail-page">
        {/* ── Hero Section ── */}
        <div className="relative h-72 sm:h-96 overflow-hidden">
          <img
            src={heroImgSrc || visuals.heroImage}
            alt={selectedLocation.name}
            className="w-full h-full object-cover"
            onError={() => setHeroImgSrc(fallbackHero)}
          />
          {/* Dark gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/30 via-slate-900/50 to-slate-900/80" />

          {/* Back link — top-left over hero */}
          <div className="absolute top-4 left-4 sm:left-8">
            <Link
              to="/locations"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900/60 hover:bg-slate-900/80 text-white text-sm rounded-md backdrop-blur-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('locations.all_locations', 'All Locations')}
            </Link>
          </div>

          {/* City name, region, and tagline over hero */}
          <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-8 pb-6">
            <h1 className="font-heading text-3xl sm:text-5xl font-extrabold text-white mb-1 drop-shadow-lg">
              {selectedLocation.name.toUpperCase()}
            </h1>
            <div className="flex items-center gap-2 text-slate-300 text-sm mb-2">
              <MapPin className="w-4 h-4" />
              <span>{selectedLocation.region}, {t('locations.germany', 'Germany')}</span>
            </div>
            {visuals.tagline && (
              <p className="text-slate-200 text-sm sm:text-base max-w-xl drop-shadow">
                {visuals.tagline}
              </p>
            )}
          </div>

          {/* Maps link — top-right over hero */}
          {selectedLocation.lat && selectedLocation.lng && (
            <div className="absolute top-4 right-4 sm:right-8">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${selectedLocation.lat},${selectedLocation.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900/60 hover:bg-slate-900/80 text-white text-sm rounded-md backdrop-blur-sm transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                {t('locations.view_on_maps', 'View on Maps')}
              </a>
            </div>
          )}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Batch 32: inbound navigation to the previously-orphan neighborhood guide. */}
          <div className="mb-6">
            <Link
              to={`/neighborhood/${selectedLocation.id}`}
              className="inline-flex items-center gap-2 text-signal-blue hover:text-signal-blue-hover text-sm font-medium"
              data-testid="location-neighborhood-guide-link"
            >
              {t('locations.view_neighborhood_guide', 'View Neighborhood Guide')}
            </Link>
          </div>
          <div className="grid lg:grid-cols-3 gap-8 mb-10">
            {/* ── Left: Overview + Bases + Lifestyle Notes ── */}
            <div className="lg:col-span-2 space-y-6">
              {locationCopy.overview && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h2 className="font-heading text-lg font-bold text-slate-800 mb-3">
                    {t('locations.about', 'About')} {selectedLocation.name}
                  </h2>
                  <p className="text-slate-600 leading-relaxed">{locationCopy.overview}</p>
                </div>
              )}

              {/* Nearby Bases */}
              {selectedLocation.nearby_bases?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <p className="label-tactical mb-3">{t('locations.nearby_military_bases', 'Nearby Military Bases')}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLocation.nearby_bases.map((base, i) => (
                      <span
                        key={i}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-slate-100 text-sm rounded-sm"
                      >
                        <Building2 className="w-3.5 h-3.5" />
                        {base}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Lifestyle Notes (from backend) */}
              {locationCopy.lifestyleNotes && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <p className="label-tactical mb-2">{t('locations.lifestyle_notes', 'Lifestyle Notes')}</p>
                  <div className="flex items-start gap-2 p-3 bg-signal-blue/10 border border-signal-blue/20 rounded-sm">
                    <Info className="w-4 h-4 text-signal-blue flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700 text-sm">{locationCopy.lifestyleNotes}</p>
                  </div>
                </div>
              )}

              <AreaIntelligenceModule
                origin={
                  Number.isFinite(selectedLocation.lat) && Number.isFinite(selectedLocation.lng)
                    ? { lat: selectedLocation.lat, lng: selectedLocation.lng }
                    : null
                }
                baseTargets={(selectedLocation.nearby_bases || []).map((name) => ({ name }))}
              />
            </div>

            {/* ── Right: Why Families Like It Here ── */}
            {visuals.lifestyleHighlights?.length > 0 && (
              <div className="space-y-4">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-heading text-base font-bold text-slate-800 mb-4">
                    {t('locations.why_families', 'Why families like it here')}
                  </h3>
                  <ul className="space-y-3">
                    {visuals.lifestyleHighlights.map((highlight, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-sm">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Local flavor chips */}
                {visuals.localFlavor?.length > 0 && (
                  <div className="bg-military-gold/10 border border-military-gold/30 rounded-xl p-4">
                    <p className="label-tactical mb-3 text-military-gold">{t('locations.local_character', 'Local character')}</p>
                    <div className="flex flex-wrap gap-2">
                      {visuals.localFlavor.map((label, i) => (
                        <span
                          key={i}
                          className="px-2.5 py-1 bg-white border border-military-gold/30 text-slate-700 text-xs rounded-full"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Properties in this Location ── */}
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-800 mb-6">
              {t('locations.properties_in', 'PROPERTIES IN')} {selectedLocation.name.toUpperCase()}
            </h2>

            {listingsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
              </div>
            ) : locationListings.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {locationListings.map((listing) => (
                  <PropertyCard key={listing.id} listing={listing} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-slate-200">
                <p className="text-slate-600 mb-4">
                  {t('locations.no_properties', 'No properties currently listed.')}
                </p>
                <Link to="/listings">
                  <Button variant="outline" className="border-slate-300 text-slate-700">
                    {t('locations.browse_all', 'Browse All Properties')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Locations List View ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="locations-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 mb-2">
            {t('locations.explore_title', 'EXPLORE LOCATIONS')}
          </h1>
          <p className="text-slate-600">
            {t('locations.explore_subtitle', 'Discover German neighborhoods near major U.S. military installations')}
          </p>
        </div>

        {/* Locations Grid */}
        {locations.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location) => (
              <LocationCard key={location.id} location={location} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-slate-200">
            <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">{t('locations.no_locations', 'No locations available yet.')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
